import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, AlertCircle } from 'lucide-react';
import { dashboardApi } from '@/api/dashboard';
import type { WatchlistItem } from '@/api/types';
import { useSSE, type SseEvent } from '@/hooks/useSSE';
import { num, pct, ago, timeOnly } from '@/lib/format';
import { cn } from '@/lib/cn';
import { RiskChip } from '@/components/RiskChip';

export function Watchlist() {
  const qc = useQueryClient();
  const { data, isLoading, isError, dataUpdatedAt } = useQuery({
    queryKey: ['watchlist'],
    queryFn: dashboardApi.watchlist,
    refetchInterval: 30_000,
  });

  // Merge live price ticks — same pattern as Holdings, but also recompute
  // each chip's distancePct so the "how close to triggering" chip stays fresh.
  useSSE('market', {
    handlers: {
      'price.tick': (e: SseEvent<{ instrumentCode?: string; currentPrice?: number }>) => {
        const code = e.payload?.instrumentCode;
        const px = e.payload?.currentPrice;
        if (!code || px == null) return;
        qc.setQueryData<WatchlistItem[]>(['watchlist'], (current) => {
          if (!current) return current;
          let touched = false;
          const next = current.map((w) => {
            if (w.code !== code) return w;
            touched = true;
            const conditions = w.conditions.map((c) => {
              if (c.thresholdValue == null || c.thresholdValue <= 0) return c;
              const distancePct = ((px - c.thresholdValue) * 100) / c.thresholdValue;
              return { ...c, distancePct };
            });
            return { ...w, currentPrice: px, conditions };
          });
          return touched ? next : current;
        });
      },
    },
  });

  return (
    <div className="p-4 lg:p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-[10px] bg-surface-2 grid place-items-center text-text-dim">
          <Eye size={20} />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-bold">Watchlist</h1>
          <div className="text-xs text-text-faint">
            {data ? `${data.length} item${data.length === 1 ? '' : 's'}` : '—'} ·
            {' '}updated {dataUpdatedAt ? ago(new Date(dataUpdatedAt).toISOString()) : '—'}
          </div>
        </div>
      </div>

      {isLoading && <div className="card text-text-dim">Loading…</div>}
      {isError && (
        <div className="card flex items-center gap-3 text-kam-red">
          <AlertCircle size={18} />
          <span>Couldn't load /api/watchlist</span>
        </div>
      )}

      {data && data.length === 0 && (
        <div className="card max-w-2xl text-text-dim">
          Nothing on the watchlist today. The PM agents add items each morning at 08:00 KST,
          and the Discovery agent adds during the day.
        </div>
      )}

      {data && data.length > 0 && (
        <div className="flex flex-col gap-3 max-w-3xl">
          {data.map((w) => (
            <div key={w.id} className="card card-tight">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{w.name}</div>
                  <div className="text-[11px] text-text-faint font-mono">
                    {w.code} · {w.market} · added {timeOnly(w.createdAt)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-base">
                    {w.currentPrice != null ? num(w.currentPrice) : '—'}
                  </div>
                  <div className="text-[10px] text-text-faint">current</div>
                </div>
              </div>

              {w.reason && (
                <div className="text-xs text-text-dim leading-relaxed mb-3">
                  {w.reason}
                </div>
              )}

              {w.conditions.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-2 border-t border-[color:var(--border)]">
                  {w.conditions.map((c) => (
                    <ChipWithDistance key={c.id} chip={c} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ChipWithDistance({ chip }: { chip: WatchlistItem['conditions'][number] }) {
  const close = chip.distancePct != null && Math.abs(chip.distancePct) < 2;
  return (
    <div className={cn('inline-flex items-center', close && 'animate-pulse')}>
      <RiskChip chip={chip} />
      {chip.distancePct != null && Math.abs(chip.distancePct) < 5 && (
        <span className={cn(
          'ml-1 text-[10px] font-mono',
          Math.abs(chip.distancePct) < 1 ? 'text-kam-amber' : 'text-text-faint',
        )}>
          {pct(chip.distancePct)}
        </span>
      )}
    </div>
  );
}
